import React, { useState, useMemo } from 'react';
import { City, Country, State } from 'country-state-city';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const CityAutocomplete = ({ value, onChange, placeholder }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // City name aliases for common translations
  const cityAliases = {
    'rome': 'Rome',
    'roma': 'Rome',
    'milan': 'Milan',
    'milano': 'Milan',
    'florence': 'Florence',
    'firenze': 'Florence',
    'venice': 'Venice',
    'venezia': 'Venice',
    'naples': 'Naples',
    'napoli': 'Naples',
    'turin': 'Turin',
    'torino': 'Turin',
    'genoa': 'Genoa',
    'genova': 'Genoa',
    'munich': 'Munich',
    'münchen': 'Munich',
    'munich': 'Munich',
    'prague': 'Prague',
    'praha': 'Prague',
    'moscow': 'Moscow',
    'moskva': 'Moscow',
    'athens': 'Athens',
    'athina': 'Athens',
    'lisbon': 'Lisbon',
    'lisboa': 'Lisbon',
    'brussels': 'Brussels',
    'bruxelles': 'Brussels',
    'brussel': 'Brussels',
    'copenhagen': 'Copenhagen',
    'københavn': 'Copenhagen'
  };
  
  // Country name aliases
  const countryAliases = {
    'italy': 'Italy',
    'italia': 'Italy',
    'france': 'France',
    'francia': 'France',
    'spain': 'Spain',
    'españa': 'Spain',
    'spagna': 'Spain',
    'germany': 'Germany',
    'germania': 'Germany',
    'deutschland': 'Germany',
    'uk': 'United Kingdom',
    'england': 'United Kingdom',
    'inghilterra': 'United Kingdom',
    'usa': 'United States',
    'america': 'United States',
    'stati uniti': 'United States'
  };

  // Get all cities from the library with country and state names
  const allCities = useMemo(() => {
    const cities = City.getAllCities();
    const countries = Country.getAllCountries();
    
    // Create lookup maps for faster access
    const countryMap = {};
    countries.forEach(c => {
      countryMap[c.isoCode] = c.name;
    });
    
    // Transform to format with full country names and aliases
    return cities.map(city => {
      const countryName = countryMap[city.countryCode] || city.countryCode;
      const stateInfo = State.getStateByCodeAndCountry(city.stateCode, city.countryCode);
      const stateName = stateInfo?.name || city.stateCode;
      
      // Build search text with aliases
      const cityNameLower = city.name.toLowerCase();
      const countryNameLower = countryName.toLowerCase();
      
      // Add all possible aliases to search text
      let searchAliases = `${city.name} ${stateName} ${countryName} ${city.countryCode}`;
      
      // Add city aliases
      Object.entries(cityAliases).forEach(([alias, canonical]) => {
        if (city.name.toLowerCase() === canonical.toLowerCase()) {
          searchAliases += ` ${alias}`;
        }
      });
      
      // Add country aliases
      Object.entries(countryAliases).forEach(([alias, canonical]) => {
        if (countryName.toLowerCase() === canonical.toLowerCase()) {
          searchAliases += ` ${alias}`;
        }
      });
      
      return {
        value: `${city.name}, ${city.stateCode}, ${city.countryCode}`,
        label: city.name,
        country: city.countryCode,
        countryName: countryName,
        state: city.stateCode,
        stateName: stateName,
        fullLabel: `${city.name}, ${stateName}, ${countryName}`,
        searchText: searchAliases.toLowerCase()
      };
    });
  }, []);

  // Filter cities based on search with intelligent multi-word matching
  const filteredCities = useMemo(() => {
    if (!searchValue) return allCities.slice(0, 100); // Show first 100 by default
    
    const searchTerms = searchValue.toLowerCase().trim().split(/\s+/);
    
    // Normalize search terms using aliases (CRITICAL FOR MULTILINGUA)
    const normalizedTerms = searchTerms.map(term => {
      // Check if term is an alias for a city
      if (cityAliases[term]) {
        return { original: term, canonical: cityAliases[term].toLowerCase(), isAlias: true, type: 'city' };
      }
      // Check if term is an alias for a country
      if (countryAliases[term]) {
        return { original: term, canonical: countryAliases[term].toLowerCase(), isAlias: true, type: 'country' };
      }
      return { original: term, canonical: term, isAlias: false, type: 'unknown' };
    });
    
    // Filter and score results
    const scored = allCities
      .map(city => {
        const cityName = city.label.toLowerCase();
        const countryName = city.countryName.toLowerCase();
        const stateName = city.stateName.toLowerCase();
        
        let score = 0;
        let matchedTerms = 0;
        let hasExactCityMatch = false;
        let hasExactCountryMatch = false;
        
        // Check if all search terms match somewhere in the city data
        for (const termObj of normalizedTerms) {
          const term = termObj.original;
          const canonical = termObj.canonical;
          let termMatched = false;
          
          // PRIORITY 1: Check canonical (aliased) exact matches
          if (termObj.isAlias && termObj.type === 'city' && cityName === canonical) {
            score += 20000; // HIGHEST PRIORITY for aliased exact city match
            hasExactCityMatch = true;
            termMatched = true;
          } 
          else if (termObj.isAlias && termObj.type === 'country' && countryName === canonical) {
            score += 1000; // HIGH PRIORITY for aliased exact country match
            hasExactCountryMatch = true;
            termMatched = true;
          }
          // PRIORITY 2: Check regular exact matches
          else if (cityName === term) {
            score += 10000;
            hasExactCityMatch = true;
            termMatched = true;
          }
          else if (countryName === term) {
            score += 100;
            hasExactCountryMatch = true;
            termMatched = true;
          }
          // PRIORITY 3: Check if term appears in searchText
          else if (city.searchText.includes(term) || city.searchText.includes(canonical)) {
            termMatched = true;
            
            if (cityName.startsWith(term)) score += 500;
            else if (cityName.includes(term)) score += 20;
            
            if (countryName.includes(term)) score += 50;
            if (stateName === term) score += 80;
            else if (stateName.includes(term)) score += 10;
          }
          
          if (termMatched) matchedTerms++;
        }
        
        // Only include if all terms matched
        if (matchedTerms === normalizedTerms.length) {
          // MEGA BONUS: Exact city + exact country (e.g., "roma italia" → Rome, Italy)
          if (hasExactCityMatch && hasExactCountryMatch) {
            score += 50000; // MASSIVE boost
          }
          return { ...city, score };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score) // Sort by relevance score
      .slice(0, 100);
    
    return scored;
  }, [searchValue, allCities]);

  const selectedCity = allCities.find(city => city.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-white hover:bg-slate-50 border-slate-200"
        >
          <div className="flex items-center gap-2 truncate">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="truncate">
              {selectedCity ? selectedCity.label : (placeholder || t('filters.city'))}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={t('filters.searchCity') || 'Search city...'}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandEmpty>
            {t('filters.noCityFound') || 'No city found.'}
          </CommandEmpty>
          <CommandList>
            <CommandGroup>
              {filteredCities.map((city) => (
                <CommandItem
                  key={city.value}
                  value={city.value}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? '' : currentValue);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Check
                    className={cn(
                      'h-4 w-4',
                      value === city.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="font-medium">{city.label}</span>
                    <span className="text-xs text-slate-500">
                      {city.stateName}, {city.countryName}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CityAutocomplete;
